import { clampAbstractCss } from '../surface';

import type { ArmContext, ArmSpec } from '../armRegistry';
import type { ProfileCard } from '../surface';

const FLAG = 'progressivewindow';
const OPEN = 'open';
const DEPTH_SOURCE = 'profile-liautotest-progressive-depth';

// A 20%-tall band across the middle of the viewport: the card under the
// reader's eye is always in it, and never more than two cards are.
const READING_BAND = '-40% 0px -40% 0px';

let observer: IntersectionObserver | null = null;
let opened = 0;
// onIntersect needs the ProfileCard onCards already read, not a fresh
// readCard(element) — arms don't import surface's readers.
let observed = new WeakMap<HTMLElement, ProfileCard>();

function readable(ctx: ArmContext): boolean {
  return ctx.cards().some((card) => card.hasHiddenAbstract);
}

function onIntersect(
  entries: IntersectionObserverEntry[],
  self: IntersectionObserver,
): void {
  entries.forEach((entry) => {
    // A card whose top has already crossed the viewport top would grow the
    // document above the reader and jump the page out from under them, so it
    // stays compressed unless they scroll back down to it. Nothing is ever
    // re-collapsed, for the same reason in reverse.
    if (!entry.isIntersecting || entry.boundingClientRect.top < 0) return;

    const element = entry.target as HTMLElement;
    const card = observed.get(element);
    if (card == null) return;

    self.unobserve(element);
    card.expandAbstract();
    card.setFlag(FLAG, OPEN);
    opened += 1;
  });
}

function reportDepthOnce(ctx: ArmContext): void {
  let reported = false;

  const report = (): void => {
    if (reported) return;
    reported = true;

    ctx.track(DEPTH_SOURCE, { extra: { cards_opened: opened } });
  };

  window.addEventListener('pagehide', report);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') report();
  });
}

export const progressiveReadingWindow: ArmSpec = {
  stamp: (ctx) => ({
    render_state: readable(ctx) ? 'rendered' : 'fallback',
  }),

  onReady: (ctx) => {
    observer = null;
    opened = 0;
    observed = new WeakMap();
    if (!readable(ctx)) return;

    // Built before the first mutation, so a browser missing the constructor
    // unwinds with the page still pixel-identical to control.
    observer = new IntersectionObserver(onIntersect, {
      rootMargin: READING_BAND,
    });

    reportDepthOnce(ctx);
  },

  onCards: (cards, ctx) => {
    const reading = observer;
    if (reading == null) return;

    cards.forEach((card) => {
      if (!card.hasHiddenAbstract) return;
      if (card.flag(FLAG) != null) return;

      const sel = card.setFlag(FLAG);
      ctx.injectStyles(
        clampAbstractCss(`${ctx.scope} ${sel}`, 2, 'abstractCollapsed'),
      );
      observed.set(card.element, card);
      reading.observe(card.element);
    });
  },
};
